/** @module Checklist of one set: owned cards as pictures, missing ones as dashed numbers. */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { PageHeader } from "@/components/page-header";
import { ProgressRing, Segmented, Switch } from "@/components/ui/controls";
import { isFullArt, variantLabel } from "@/lib/cards";
import { count } from "@/lib/format";

type Show = "all" | "missing" | "owned";
type Entry = components["schemas"]["ChecklistEntryOut"];

/** One cell of the grid: a card, or in the master set one variant of it. */
interface Cell {
  key: string;
  printId: string;
  localId: string;
  quantity: number;
  rarityClass: string | null;
  /** Set in the master set: which variant this cell stands for. */
  variant?: string;
}

async function fetchSet(setId: string) {
  const { data, response } = await api.GET("/api/v1/sets/{set_id}", {
    params: { path: { set_id: setId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

async function fetchCards(setId: string) {
  const { data, response } = await api.GET("/api/v1/sets/{set_id}/cards", {
    params: { path: { set_id: setId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

async function fetchChecklist(setId: string) {
  const { data, response } = await api.GET("/api/v1/collection/checklists/{set_id}", {
    params: { path: { set_id: setId } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** The grid's cells: one per card, or one per variant when the master set is on. */
export function cellsOf(items: Entry[], master: boolean): Cell[] {
  if (!master) {
    return items.map((item) => ({
      key: item.print_id,
      printId: item.print_id,
      localId: item.local_id,
      quantity: item.quantity,
      rarityClass: item.rarity_class,
    }));
  }
  return items.flatMap((item) =>
    (item.variants ?? []).map((variant) => ({
      key: `${item.print_id}-${String(variant.variant_id)}`,
      printId: item.print_id,
      localId: item.local_id,
      quantity: variant.quantity,
      rarityClass: item.rarity_class,
      variant: variant.variant_key,
    })),
  );
}

export function SetPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const { setId = "" } = useParams({ strict: false });
  const [show, setShow] = useState<Show>("all");
  const [master, setMaster] = useState(false);
  const set = useQuery({ queryKey: ["set", setId, locale], queryFn: () => fetchSet(setId) });
  const cards = useQuery({
    queryKey: ["set-cards", setId, locale],
    queryFn: () => fetchCards(setId),
  });
  const checklist = useQuery({
    queryKey: ["checklist", setId],
    queryFn: () => fetchChecklist(setId),
  });

  if (set.isPending || checklist.isPending) return <p className="t-muted">{t("common.loading")}</p>;
  if (set.isError || checklist.isError)
    return (
      <p role="alert" className="loss">
        {t("common.loadError")}
      </p>
    );

  const names = new Map((cards.data ?? []).map((card) => [card.id, card.name]));
  const data = checklist.data;
  const cells = cellsOf(data.items, master);
  const total = master ? data.variants_total : data.total;
  const have = master ? data.variants_owned : data.owned;
  const missing = total - have;
  const percent = total ? (have / total) * 100 : 0;
  const shown = cells.filter(
    (cell) => show === "all" || (show === "owned" ? cell.quantity > 0 : cell.quantity === 0),
  );
  const label = (key: string) => variantLabel(key, (k, fallback) => t(k, fallback));

  return (
    <div className="flex flex-col gap-[14px]">
      <PageHeader
        global={false}
        back={
          <Link to="/sets" className="btn btn-ghost btn-sm">
            <ChevronLeft />
            {t("nav.sets")}
          </Link>
        }
      />
      <div className="row gap-[14px]">
        <ProgressRing percent={percent} onPage />
        <span className="flex min-w-0 flex-col">
          <h1 className="t-h2 md:text-[length:var(--text-3xl)]">{set.data.name}</h1>
          <span className="t-sm t-muted">
            <span className="num">{set.data.abbreviation ?? set.data.id}</span>
            {set.data.release_date ? ` · ${set.data.release_date.slice(0, 4)}` : ""}
            {" · "}
            <span className="num">
              {count(data.owned, locale)}/{count(data.total, locale)}
            </span>{" "}
            {t("catalog.cards")}
          </span>
        </span>
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Segmented
          className="self-start"
          label={t("catalog.showLabel")}
          value={show}
          onChange={setShow}
          options={[
            { value: "all", label: t("catalog.show.all") },
            {
              value: "missing",
              label: t("catalog.missingCount", { count: missing, shown: count(missing, locale) }),
            },
            { value: "owned", label: t("catalog.show.owned") },
          ]}
        />
        <div className="row-between gap-4 md:justify-end">
          <span className="flex flex-col">
            <span className="t-sm">{t("catalog.masterSet")}</span>
            <span className="t-xs t-muted num">
              {t("catalog.variantsOwned", {
                owned: count(data.variants_owned, locale),
                total: count(data.variants_total, locale),
              })}
            </span>
          </span>
          <Switch checked={master} label={t("catalog.masterSet")} onChange={setMaster} />
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="t-muted">
          {show === "missing" ? t("catalog.nothingMissing") : t("catalog.nothingOwned")}
        </p>
      ) : (
        <ul className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-8 xl:grid-cols-10 2xl:grid-cols-12">
          {shown.map((cell) => {
            const name = names.get(cell.printId) ?? cell.printId;
            const caption = cell.variant
              ? `${cell.localId} · ${label(cell.variant)}`
              : cell.localId;
            return (
              <li key={cell.key}>
                <Link
                  to="/cards/$printId"
                  params={{ printId: cell.printId }}
                  title={`${name} · ${caption}`}
                  aria-label={`${name} · ${caption}${cell.quantity === 0 ? ` · ${t("catalog.missing")}` : ""}`}
                  className="block text-(--text) no-underline"
                >
                  {cell.quantity > 0 ? (
                    <span
                      className={`card-shape relative block ${isFullArt(cell.rarityClass) ? "foil" : ""}`}
                    >
                      <CardFace printId={cell.printId} name={name} lazy />
                      <span className="cl-num">{caption}</span>
                      {cell.quantity > 1 ? (
                        <span className="badge badge-qty tile-qty">{cell.quantity}×</span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="cl-miss">{caption}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
